import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ModalComponent from '../../../../components/ModalComponent';
import zod from 'zod';
import { AppContext } from '../../../../context/AppContextProvider';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2, Typography } from '@mui/material';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import { SportHallFormSchema } from '../../../../services/Sports/Halls/schemas';
import { ISportHallProps } from '../../../../services/Sports/Halls/types';
import {
  useCreateSportHall,
  useUpdateSportHall,
} from '../../../../services/Sports/Halls/hooks/useMutation';
import { IIvsDeviceSimpleProps } from '../../../../services/Devices/IvsService/types';
import { ISportUnitySimpleProps } from '../../../../services/Sports/Unities/types';
import { useGetAllSportUnitiesByLicenseAsync } from '../../../../services/Sports/Unities/hooks/useMutation';
import { useGetAllDevicesByLicenseAsync } from '../../../../services/Devices/hooks/useMutation';
import {
  IDeviceFilterParams,
  IDeviceGetParams,
  IDeviceSimpleProps,
} from '../../../../services/Devices/types';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../../components/DropSelectComponent';
import SportHallAdvertisingEdition from './SportHallAdvertisingEdition';
import Divider from '@mui/material/Divider';
import InformationComponent from '../../../../components/InformationComponent';
import { t } from 'i18next';

interface ISportUnityEditModal {
  isOpen: boolean;
  onClose: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleSaveForm: () => void;
  selectedHall?: ISportHallProps;
}

export type SportHallFormInput = zod.infer<typeof SportHallFormSchema>;

interface IDeviceItems {
  label: string;
  value: any;
}

export const SportHallEditModal = ({
  isOpen,
  onClose,
  handleSaveForm,
  selectedHall,
}: ISportUnityEditModal) => {
  const methods = useForm<SportHallFormInput>({
    resolver: zodResolver(SportHallFormSchema),
    defaultValues: {
      name: '',
      deviceIds: [],
      sportUnityId: undefined,
      devices: [],
      playbackTime: 20,
      videoAdvertisingImages: [],
    },
  });
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>();
  const [deviceItems, setDeviceItems] = useState<IDeviceItems[]>([]);
  const [unities, setUnities] = useState<ISportUnitySimpleProps[]>();
  const [filterDevices, setFilterDevices] = useState<IDeviceFilterParams>({
    name: '',
    types: ['IVS', 'DVR'],
  });
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const { mutateAsync: mutateCreateHall } = useCreateSportHall();
  const { mutateAsync: mutateUpdateHall } = useUpdateSportHall();
  const { mutateAsync: mutateUnities } = useGetAllSportUnitiesByLicenseAsync();
  const { mutateAsync: mutateDevices } = useGetAllDevicesByLicenseAsync();

  const playbackTimeOptions: IDropSelectOptions[] = [
    {
      option: t(
        'sportsPage.blocks.sportBlockModal.playbackTimeOptions.option1',
      ),
      value: 20,
    },
    {
      option: t(
        'sportsPage.blocks.sportBlockModal.playbackTimeOptions.option2',
      ),
      value: 30,
    },
    {
      option: t(
        'sportsPage.blocks.sportBlockModal.playbackTimeOptions.option3',
      ),
      value: 40,
    },
  ];

  const deviceOptions: IDropSelectOptions[] = useMemo(() => {
    if (devices) {
      return devices.map((i) => {
        return {
          option: i.name,
          value: i.id,
          description: i.type,
        };
      });
    }
    return [];
  }, [devices]);

  const unityOptions: IDropSelectOptions[] = useMemo(() => {
    if (unities) {
      return unities.map((i) => {
        return {
          option: i.name,
          value: i.id,
        };
      });
    }
    return [];
  }, [unities]);

  const refetchUnities = useCallback(async () => {
    await mutateUnities({}).then(setUnities);
  }, []);

  const refetchDevices = useCallback(async () => {
    await mutateDevices({
      undefined,
      filter: filterDevices,
    } as IDeviceGetParams).then(setDevices);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleEdition = async (data: SportHallFormInput) => {
    setIsShowLoadingComponent(true);
    if (selectedHall) {
      await mutateUpdateHall({
        id: selectedHall.id,
        data,
      })
        .then(() => {
          resetAndShowMessage(
            t('sportsPage.blocks.sportBlockModal.sportsCourtSuccessUpdate'),
          );
        })
        .finally(() => setIsShowLoadingComponent(false));
    } else {
      mutateCreateHall(data)
        .then(() => {
          resetAndShowMessage(
            t('sportsPage.blocks.sportBlockModal.sportsCourtSuccessCreate'),
          );
        })
        .finally(() => setIsShowLoadingComponent(false));
    }
  };

  const resetAndShowMessage = (message: string) => {
    reset();
    onClose();
    showSnackbar(message, 'success');
    handleSaveForm();
  };

  const loadDevices = (devices: IIvsDeviceSimpleProps[]) => {
    const ids = devices.map((device) => device.id);
    if (ids.length > 0) {
      setValue('deviceIds', [ids[0], ...ids.slice(1)]);
    }
  };

  const loadUnity = (unity: ISportUnitySimpleProps) => {
    setValue('sportUnityId', unity.id);
  };

  useEffect(() => {
    if (
      devices &&
      devices.length > 0 &&
      (!deviceItems || deviceItems.length === 0)
    ) {
      const items: IDeviceItems[] = devices.map((item) => ({
        label: `${item.name} - ${item.type}`,
        value: item.id,
      }));
      setDeviceItems(items);
    }
  }, [devices, deviceItems]);

  useEffect(() => {
    refetchUnities();
    refetchDevices();
  }, [refetchUnities, refetchDevices]);

  useEffect(() => {
    if (selectedHall && selectedHall.id) {
      reset(selectedHall, { keepDefaultValues: true });
      loadUnity(selectedHall.sportUnity);
      loadDevices(selectedHall.devices);
    } else {
      reset();
    }
  }, [selectedHall]);

  return (
    <ModalComponent
      title={
        selectedHall
          ? t('sportsPage.blocks.sportBlockModal.editTitle')
          : t('sportsPage.blocks.sportBlockModal.addTitle')
      }
      onClose={handleClose}
      isOpen={isOpen}
    >
      <Grid2
        container
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={2}
      >
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label={t('sportsPage.blocks.sportBlockModal.name')}
                onChangeText={(val) => onChange(val)}
                type={'text'}
                errorMessage={error?.message}
                value={value}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                label={t('sportsPage.blocks.sportBlockModal.unitySports')}
                options={unityOptions}
                errorMessage={error?.message}
                valueSelected={value}
                onChange={onChange}
                mb={0}
              />
            )}
            name={'sportUnityId'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                label={t('sportsPage.blocks.sportBlockModal.devices')}
                options={deviceOptions}
                valueSelected={value}
                onChange={onChange}
                isMultiple={true}
                errorMessage={error?.message}
                mb={0}
              />
            )}
            name={'deviceIds'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropSelectComponent
                label={t('sportsPage.blocks.sportBlockModal.playbackTime')}
                options={playbackTimeOptions}
                valueSelected={value}
                onChange={onChange}
                errorMessage={error?.message}
                mb={0}
              />
            )}
            name={'playbackTime'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12}>
          <Grid2 container direction={'row'} gap={1} size={12} mb={1}>
            <Typography variant={'h5'} component={'p'}>
              {t('sportsPage.blocks.sportBlockModal.advertising')}
            </Typography>
            <InformationComponent
              information={t(
                'sportsPage.blocks.sportBlockModal.advertisingInfo',
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <SportHallAdvertisingEdition
                  advertisingImages={value}
                  onChange={onChange}
                />
              )}
              name={'videoAdvertisingImages'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <ButtonComponent
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit(handleEdition)}
          >
            {t('sportsPage.blocks.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};
